import { type FlowDynamicValue, FlowDynamicValueType } from 'flow-schema';

import { api, assertNever, isDefined } from 'common';

import { ScriptContextType } from './ScriptContext';
import { tryParseJson } from '../../../../../../../utils/json';

const { FlowKeywords } = api.FlowDefinition;

interface DynamicValueTypeOption {
  value: FlowDynamicValueType;
  label: string;
  helperText: string;
}

const AutoTypeOption: DynamicValueTypeOption = {
  value: FlowDynamicValueType.Auto,
  label: 'Auto',
  helperText: 'Either a placeholder or a fixed value specified here',
};

const IterationTypeOptions: readonly DynamicValueTypeOption[] = [
  {
    value: FlowDynamicValueType.Element,
    label: 'Element',
    helperText: 'The current array element within the iteration',
  },
  {
    value: FlowDynamicValueType.Block,
    label: 'Block',
    helperText: 'The data from a selected block',
  },
  {
    value: FlowDynamicValueType.Expression,
    label: 'Reference',
    helperText: 'The value of a specified block, input, or subfield thereof',
  },
  {
    value: FlowDynamicValueType.Literal,
    label: 'Constant',
    helperText: 'A fixed value specified here',
  },
];

const BaseTypeOptions = IterationTypeOptions.filter(
  ({ value }) => value !== FlowDynamicValueType.Element
);

export function getDynamicValueTypeOptions(args: {
  scriptContextType: ScriptContextType | undefined;
  isAutoDynamicValueTypeEnabled: boolean;
  currentType?: FlowDynamicValueType;
}): readonly DynamicValueTypeOption[] | undefined {
  const { scriptContextType, isAutoDynamicValueTypeEnabled, currentType } = args;
  const typeOptions =
    scriptContextType === ScriptContextType.Iteration ? IterationTypeOptions : BaseTypeOptions;
  if (isAutoDynamicValueTypeEnabled) {
    if (!isDefined(currentType) || currentType === FlowDynamicValueType.Auto) {
      return undefined;
    }
    // Non-Auto type not yet converted
    return [AutoTypeOption, ...typeOptions];
  }
  if (currentType === FlowDynamicValueType.Auto) {
    // Auto type for user without it enabled yet
    return [AutoTypeOption, ...typeOptions];
  }
  return typeOptions;
}

export function createDynamicValue(type: FlowDynamicValueType | undefined): FlowDynamicValue {
  if (!type) {
    return { type: undefined };
  }
  switch (type) {
    case FlowDynamicValueType.Auto:
      return { type, jsonOrTemplate: '' };
    case FlowDynamicValueType.Block:
      return { type, blockName: '' };
    case FlowDynamicValueType.Element:
      return { type };
    case FlowDynamicValueType.Expression:
      return { type, expression: '' };
    case FlowDynamicValueType.Literal:
      return { type, value: undefined, json: '' };
    case FlowDynamicValueType.Template:
      return { type, template: '' };
    default:
      assertNever('Invalid dynamic value type', type);
  }
}

export function convertDynamicValue(
  value: FlowDynamicValue | undefined,
  newType: FlowDynamicValueType | undefined
): FlowDynamicValue {
  if (!isDefined(newType)) {
    return { type: undefined };
  }
  if (value) {
    const oldType = value.type;
    if (oldType === FlowDynamicValueType.Auto && value.jsonOrTemplate) {
      const { jsonOrTemplate } = value;
      if (newType === FlowDynamicValueType.Block) {
        const blockName = /^\{\{\s*(\w+)\.data\s*\}\}$/g.exec(jsonOrTemplate)?.[1] ?? '';
        return { type: newType, blockName };
      } else if (newType === FlowDynamicValueType.Expression) {
        const expression = /^\{\{([^{}]*)\}\}$/g.exec(jsonOrTemplate)?.[1] ?? '';
        return { type: newType, expression };
      } else if (newType === FlowDynamicValueType.Literal) {
        return { type: newType, json: jsonOrTemplate, value: tryParseJson(jsonOrTemplate).value };
      } else if (newType === FlowDynamicValueType.Template) {
        return { type: newType, template: jsonOrTemplate };
      }
    } else if (oldType === FlowDynamicValueType.Block && value.blockName) {
      if (newType === FlowDynamicValueType.Auto) {
        return { type: newType, jsonOrTemplate: `{{${value.blockName}.data}}` };
      } else if (newType === FlowDynamicValueType.Template) {
        return { type: newType, template: `{{${value.blockName}.data}}` };
      } else if (newType === FlowDynamicValueType.Expression) {
        return { type: newType, expression: `${value.blockName}.data` };
      }
    } else if (oldType === FlowDynamicValueType.Element) {
      if (newType === FlowDynamicValueType.Auto) {
        return { type: newType, jsonOrTemplate: `{{${FlowKeywords.element}}}` };
      } else if (newType === FlowDynamicValueType.Expression) {
        return { type: newType, expression: FlowKeywords.element };
      } else if (newType === FlowDynamicValueType.Template) {
        return { type: newType, template: `{{${FlowKeywords.element}}}` };
      }
    } else if (oldType === FlowDynamicValueType.Expression && value.expression) {
      if (newType === FlowDynamicValueType.Auto) {
        return { type: newType, jsonOrTemplate: `{{${value.expression}}}` };
      } else if (newType === FlowDynamicValueType.Block) {
        const blockName = /^\s*(\w+)\.data\s*$/g.exec(value.expression)?.[1] ?? '';
        return { type: newType, blockName };
      } else if (newType === FlowDynamicValueType.Template) {
        return { type: newType, template: `{{${value.expression}}}` };
      }
    } else if (oldType === FlowDynamicValueType.Literal && value.json) {
      if (newType === FlowDynamicValueType.Auto) {
        return { type: newType, jsonOrTemplate: value.json };
      } else if (newType === FlowDynamicValueType.Template) {
        const result = tryParseJson(value.json).value;
        return { type: newType, template: typeof result === 'string' ? result : value.json };
      }
    } else if (oldType === FlowDynamicValueType.Template && value.template) {
      if (newType === FlowDynamicValueType.Auto) {
        return { type: newType, jsonOrTemplate: value.template };
      } else if (newType === FlowDynamicValueType.Block) {
        const blockName = /^\{\{\s*(\w+)\.data\s*\}\}$/g.exec(value.template)?.[1] ?? '';
        return { type: newType, blockName };
      } else if (newType === FlowDynamicValueType.Expression) {
        const expression = /^\{\{([^{}]*)\}\}$/g.exec(value.template)?.[1] ?? '';
        return { type: newType, expression };
      } else if (newType === FlowDynamicValueType.Literal) {
        return { type: newType, value: value.template, json: JSON.stringify(value.template) };
      }
    }
  }
  return createDynamicValue(newType);
}
